import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid4 } from 'uuid';

import { ImageButton } from '@components/imagebutton';
import { ChevronIcon } from '@components/chevronicon';
import Tag from '@components/tag/tag';
import ReviewTag from '@components/reviewtag/reviewtag';
import { getVehicleSiteCoreDetails } from '@services/vehicleservice';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { updateVehicleSiteCoreDetailsTags } from '@store/reducers/vehicleinformation';

import { useReviewScore } from '@hooks/usereviewscore';
import cn from 'classnames';

import '@styles/configure/toolbar.scss';
import { Tag as TagData } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/tag';
import { VehicleProps, VehicleSiteCoreDetails } from 'interface';

const Toolbar: React.FC<VehicleProps> = ({ vehicle }) => {
  const { id, capId, stockLeadTime } = vehicle;
  const { ready, score } = useReviewScore(capId);

  const dispatch = useAppDispatch();
  const vehicleInformation = useAppSelector(
    (state) => state.vehicleInformation
  );
  const { referrer } = useAppSelector((state) => state.navigate);

  const [vehicleTags, setVehicleTags] = useState<TagData[] | undefined>(
    vehicleInformation.siteCoreDetails?.tags
  );

  const hasFetchedData = useRef(false);
  const noStockLeadTimeLabel = 'Contact us for lead time';
  const deliveryLabel: string = 'Delivery';
  const carwowLabel: string = 'Carwow';

  useEffect(() => {
    if (
      !hasFetchedData.current &&
      vehicleInformation.siteCoreDetails &&
      !vehicleInformation.siteCoreDetails.id
    ) {
      getVehicleSiteCoreDetails(id)
        .then((res: VehicleSiteCoreDetails) => {
          let { tags } = res;
          if (tags && tags.length > 0) {
            if (!tags.some((tag) => tag.type === deliveryLabel)) {
              tags = [...tags, { type: deliveryLabel, label: deliveryLabel }];
            }
            setVehicleTags(tags);
            dispatch(updateVehicleSiteCoreDetailsTags({ id, tags }));
          }
        })
        .catch(() => {
          setVehicleTags([]);
        });
    }
  }, [id]);

  const titleCase = (text: string): string => {
    let textArray: string[] = text.toLowerCase().split(' ');
    for (var i = 0; i < textArray.length; i++) {
      textArray[i] =
        textArray[i].charAt(0).toUpperCase() + textArray[i].slice(1);
    }
    return textArray.join(' ');
  };

  const getTags = (): TagData[] => {
    const tags =
      vehicleTags && vehicleTags.length > 0
        ? vehicleTags.filter((tag) => tag.type !== carwowLabel)
        : [];

    return tags;
  };

  const handleBack = () => {
    window.location.href = referrer;
  };

  return (
    <section
      className="configure-toolbar wide-component-container"
      data-testid="toolbar"
    >
      <div className="configure-toolbar__back-link" data-cy="back-navigation">
        <ImageButton
          variant="text"
          size="small"
          ButtonIcon={ChevronIcon}
          onClick={handleBack}
          ariaLabel="Back"
        >
          Back
        </ImageButton>
      </div>
      <div className="configure-toolbar__tag-container">
        {ready && getTags().length > 0
          ? getTags().map((tag) => {
              const content =
                tag.type == deliveryLabel
                  ? stockLeadTime
                    ? `${titleCase(stockLeadTime)} ${tag.label}`
                    : noStockLeadTimeLabel
                  : tag.label;

              return (
                <Tag
                  key={uuid4()}
                  classNames="configure-toolbar__tag"
                  type={tag.type}
                >
                  {content}
                </Tag>
              );
            })
          : ready && (
              <Tag classNames="configure-toolbar__tag" type={deliveryLabel}>
                {stockLeadTime
                  ? `${titleCase(stockLeadTime)} Delivery`
                  : noStockLeadTimeLabel}
              </Tag>
            )}
        {ready && (
          <ReviewTag
            classNames={cn(
              'configure-toolbar__tag',
              getTags().filter((tag) => tag.type !== deliveryLabel).length >
                0 && 'tag__mobile-hidden'
            )}
            data-cy="review"
            rating={score}
          />
        )}
      </div>
    </section>
  );
};

export default Toolbar;
